// src/pages/public/Checkout.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import PixIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { get, ref } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";
import { createOrder } from "../../services/tickets/ticketsService";

function fmt(v) {
  return Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatDate(ms) {
  if (!ms) return "Data a definir";
  return new Date(Number(ms)).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ─── Componente de método de pagamento ────────────────────────────────────────

function PaymentMethodCard({ method, selected, onClick, icon, label, desc }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `2px solid ${selected ? "var(--verdeClaro)" : "rgba(255,255,255,0.10)"}`,
        background: selected ? "rgba(26,255,122,0.06)" : "rgba(255,255,255,0.03)",
        cursor: "pointer",
        transition: "all .2s",
        "&:hover": { borderColor: "rgba(26,255,122,0.4)" },
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box sx={{ color: selected ? "var(--verdeClaro)" : "text.secondary" }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 900, fontSize: 15 }}>{label}</Typography>
        <Typography sx={{ fontSize: 12, opacity: 0.7 }}>{desc}</Typography>
      </Box>
      {selected && (
        <CheckCircleIcon sx={{ ml: "auto", color: "var(--verdeClaro)", fontSize: 20 }} />
      )}
    </Box>
  );
}

// ─── Tela de Pix ──────────────────────────────────────────────────────────────

function PixScreen({ total }) {
  const [copied, setCopied] = useState(false);

  const fakeCode =
    "00020126580014br.gov.bcb.pix0136onurb-pix-key@onurbpass.com.br5204000053039865802BR5925ONURB TECNOLOGIA LTDA6009SAO PAULO62070503***6304ONURB";

  async function handleCopy() {
    try { await navigator.clipboard.writeText(fakeCode); } catch (_) {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  return (
    <Box sx={{ textAlign: "center" }}>
      <Typography sx={{ fontWeight: 900, mb: 1 }}>Pague via Pix</Typography>
      <Typography sx={{ opacity: 0.7, fontSize: 13, mb: 2 }}>
        Escaneie o QR abaixo ou copie o código copia-e-cola
      </Typography>

      <Box
        sx={{
          display: "inline-block",
          p: 2,
          borderRadius: 2,
          border: "1px solid rgba(255,255,255,0.12)",
          background: "#fff",
          mb: 2,
        }}
      >
        <Box
          component="img"
          src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(fakeCode)}`}
          alt="QR Code Pix"
          sx={{ display: "block", width: 180, height: 180 }}
        />
      </Box>

      <Typography sx={{ fontWeight: 950, fontSize: 20, color: "var(--verdeClaro)", mb: 1.5 }}>
        {fmt(total)}
      </Typography>

      <Box
        sx={{
          p: 1.5,
          borderRadius: 1.5,
          border: "1px dashed rgba(255,255,255,0.20)",
          background: "rgba(255,255,255,0.03)",
          fontFamily: "monospace",
          fontSize: 11,
          wordBreak: "break-all",
          textAlign: "left",
          mb: 1.2,
          opacity: 0.8,
        }}
      >
        {fakeCode}
      </Box>

      <Button
        fullWidth
        variant="outlined"
        size="small"
        startIcon={copied ? <DoneRoundedIcon /> : <ContentCopyRoundedIcon />}
        onClick={handleCopy}
        sx={{
          mb: 2,
          borderRadius: 1.5,
          fontWeight: 900,
          borderColor: copied ? "rgba(26,255,122,0.6)" : "rgba(255,255,255,0.22)",
          color: copied ? "var(--verdeClaro)" : "inherit",
          background: copied ? "rgba(26,255,122,0.08)" : "transparent",
          transition: "all .2s",
          "&:hover": {
            borderColor: "rgba(26,255,122,0.6)",
            background: "rgba(26,255,122,0.08)",
          },
        }}
      >
        {copied ? "Código copiado!" : "Copiar código Pix"}
      </Button>

      <Typography sx={{ fontSize: 12, opacity: 0.6 }}>
        Válido por 30 minutos · Pagamento simulado (ambiente de testes)
      </Typography>
    </Box>
  );
}

// ─── Formulário de cartão ──────────────────────────────────────────────────────

function CardForm({ card, onChange }) {
  function fmtCard(v) {
    return (v || "").replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
  }
  function fmtExp(v) {
    const d = (v || "").replace(/\D/g, "").slice(0, 4);
    if (d.length <= 2) return d;
    return `${d.slice(0, 2)}/${d.slice(2)}`;
  }

  return (
    <Stack spacing={1.5}>
      <TextField
        label="Número do cartão"
        value={card.number}
        onChange={(e) => onChange("number", fmtCard(e.target.value))}
        inputProps={{ inputMode: "numeric" }}
        placeholder="0000 0000 0000 0000"
        fullWidth
      />
      <TextField
        label="Nome no cartão"
        value={card.name}
        onChange={(e) => onChange("name", e.target.value.toUpperCase())}
        placeholder="NOME COMPLETO"
        fullWidth
      />
      <Stack direction="row" spacing={1.5}>
        <TextField
          label="Validade"
          value={card.expiry}
          onChange={(e) => onChange("expiry", fmtExp(e.target.value))}
          placeholder="MM/AA"
          inputProps={{ inputMode: "numeric" }}
          fullWidth
        />
        <TextField
          label="CVV"
          value={card.cvv}
          onChange={(e) => onChange("cvv", e.target.value.replace(/\D/g, "").slice(0, 4))}
          inputProps={{ inputMode: "numeric" }}
          fullWidth
        />
      </Stack>
      <Typography sx={{ fontSize: 11, opacity: 0.55 }}>
        Dados fictícios · Nenhuma cobrança real será efetuada
      </Typography>
    </Stack>
  );
}

// ─── Página principal ──────────────────────────────────────────────────────────

export default function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();

  const [userId, setUserId] = useState(null);
  const [cart, setCart] = useState(location.state?.cart || null);
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "", cpf: "" });
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [card, setCard] = useState({ number: "", name: "", expiry: "", cvv: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // carrega carrinho do localStorage se não veio via state
  useEffect(() => {
    if (location.state?.cart) {
      setCart(location.state.cart);
      return;
    }
    try {
      const stored = localStorage.getItem("onurbpass_cart");
      if (stored) setCart(JSON.parse(stored));
    } catch (_) {}
  }, [location.state]);

  // preenche dados do comprador a partir do perfil
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user?.uid) return;
      setUserId(user.uid);
      try {
        const snap = await get(ref(rtdb, `users/${user.uid}`));
        if (!snap.exists()) return;
        const p = snap.val() || {};
        setBuyer((prev) => ({
          name: p.name || prev.name,
          email: p.email || prev.email,
          phone: p.phone || prev.phone,
          cpf: p.cpf || prev.cpf,
        }));
      } catch (_) {}
    });
    return () => unsub();
  }, []);

  const isCartEmpty = !cart || !Array.isArray(cart.items) || cart.items.length === 0;

  const cardValid =
    paymentMethod !== "credit" ||
    (card.number.replace(/\s/g, "").length === 16 &&
      card.name.trim().length > 2 &&
      card.expiry.length === 5 &&
      card.cvv.length >= 3);

  const canConfirm = useMemo(
    () =>
      Boolean(
        !isCartEmpty &&
          buyer.name.trim() &&
          buyer.email.trim() &&
          buyer.phone.trim() &&
          buyer.cpf.trim() &&
          cardValid,
      ),
    [buyer, isCartEmpty, cardValid],
  );

  async function handleConfirm() {
    if (!canConfirm || submitting || !userId) return;
    setSubmitting(true);
    setError("");
    try {
      const result = await createOrder({ cart, buyer, paymentMethod, userId });
      localStorage.removeItem("onurbpass_cart");
      navigate("/checkout/sucesso", {
        state: { orderId: result.orderId, tickets: result.tickets, cart, buyer },
      });
    } catch (err) {
      console.error(err);
      setError("Falha ao confirmar pedido. Tente novamente.");
      setSubmitting(false);
    }
  }

  if (isCartEmpty) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
            Carrinho vazio
          </Typography>
          <Typography sx={{ opacity: 0.75, mb: 3 }}>
            Selecione um ingresso antes de seguir para o checkout.
          </Typography>
          <Button variant="contained" onClick={() => navigate("/eventos")}>
            Ver eventos
          </Button>
        </Paper>
      </Container>
    );
  }

  const total = cart.summary?.total ?? 0;
  const feePercent = cart.summary?.feePercent ?? 3;

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {/* ─── COLUNA ESQUERDA ─── */}
        <Stack spacing={2.5}>
          {/* Header */}
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 950, letterSpacing: -0.8 }}>
              Checkout
            </Typography>
            <Typography sx={{ opacity: 0.7, mt: 0.5 }}>
              Revise o pedido e escolha como pagar.
            </Typography>
          </Box>

          {/* Dados do comprador */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 900, mb: 2 }}>
              Dados do comprador
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  label="Nome completo"
                  value={buyer.name}
                  onChange={(e) => setBuyer((p) => ({ ...p, name: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="E-mail"
                  type="email"
                  value={buyer.email}
                  onChange={(e) => setBuyer((p) => ({ ...p, email: e.target.value }))}
                  fullWidth
                />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  label="WhatsApp"
                  value={buyer.phone}
                  onChange={(e) => setBuyer((p) => ({ ...p, phone: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label="CPF"
                  value={buyer.cpf}
                  onChange={(e) => setBuyer((p) => ({ ...p, cpf: e.target.value }))}
                  fullWidth
                />
              </Stack>
            </Stack>
          </Paper>

          {/* Ingressos */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 900, mb: 2 }}>Ingressos</Typography>

            <Box
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 1.5,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.03)",
              }}
            >
              <Typography sx={{ fontWeight: 900 }}>{cart.event?.title}</Typography>
              <Typography sx={{ opacity: 0.65, fontSize: 13 }}>
                {formatDate(cart.event?.startAtMs)}
              </Typography>
              {cart.event?.location?.city && (
                <Typography sx={{ opacity: 0.65, fontSize: 13 }}>
                  {cart.event.location.city} · {cart.event.location.state}
                </Typography>
              )}
            </Box>

            <Stack spacing={1}>
              {cart.items.map((item) => (
                <Box
                  key={item.ticketId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1.5,
                    borderRadius: 1.5,
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>{item.name}</Typography>
                    <Typography sx={{ opacity: 0.65, fontSize: 13 }}>
                      {item.lot} · {item.quantity}x {fmt(item.unitPrice)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 900 }}>{fmt(item.lineTotal)}</Typography>
                </Box>
              ))}
            </Stack>
          </Paper>

          {/* Método de pagamento */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 900, mb: 2 }}>
              Forma de pagamento
            </Typography>

            <Stack spacing={1.2} sx={{ mb: 2 }}>
              <PaymentMethodCard
                method="pix"
                selected={paymentMethod === "pix"}
                onClick={() => setPaymentMethod("pix")}
                icon={<PixIcon />}
                label="Pix"
                desc="Aprovação instantânea · Sem taxas extras"
              />
              <PaymentMethodCard
                method="credit"
                selected={paymentMethod === "credit"}
                onClick={() => setPaymentMethod("credit")}
                icon={<CreditCardIcon />}
                label="Cartão de crédito"
                desc="Em até 3x sem juros"
              />
            </Stack>

            {paymentMethod === "pix" && (
              <Box sx={{ mt: 2 }}>
                <PixScreen total={total} />
              </Box>
            )}

            {paymentMethod === "credit" && (
              <Box sx={{ mt: 2 }}>
                <CardForm
                  card={card}
                  onChange={(field, val) => setCard((p) => ({ ...p, [field]: val }))}
                />
              </Box>
            )}
          </Paper>
        </Stack>

        {/* ─── COLUNA DIREITA (sticky) ─── */}
        <Box sx={{ position: { md: "sticky" }, top: { md: 90 } }}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 900, mb: 1.5 }}>
              Resumo do pedido
            </Typography>

            <Typography sx={{ fontWeight: 800, mb: 0.4 }}>
              {cart.event?.title}
            </Typography>
            <Typography sx={{ opacity: 0.65, fontSize: 13, mb: 2 }}>
              {formatDate(cart.event?.startAtMs)}
            </Typography>

            <Stack spacing={1}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography>Ingressos</Typography>
                <Typography sx={{ fontWeight: 800 }}>
                  {cart.summary?.totalTickets}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography>Subtotal</Typography>
                <Typography sx={{ fontWeight: 800 }}>
                  {fmt(cart.summary?.subtotal)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography>Taxa ONURB ({feePercent}%)</Typography>
                <Typography sx={{ fontWeight: 800 }}>
                  {fmt(cart.summary?.feeAmount)}
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2.5 }}>
              <Typography sx={{ fontWeight: 950, fontSize: 18 }}>Total</Typography>
              <Typography sx={{ fontWeight: 950, fontSize: 18 }}>
                {fmt(total)}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 1.5, borderRadius: 1.5 }}>
                {error}
              </Alert>
            )}

            <Button
              fullWidth
              size="large"
              onClick={handleConfirm}
              disabled={!canConfirm || submitting}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontWeight: 950,
                background: "linear-gradient(-135deg, var(--verdeClaro), #6dffb9)",
                color: "#1e1e1e",
                "&:disabled": {
                  background: "rgba(255,255,255,0.10)",
                  color: "rgba(255,255,255,0.3)",
                },
              }}
            >
              {submitting ? (
                <CircularProgress size={20} sx={{ color: "#1e1e1e" }} />
              ) : (
                "Confirmar compra"
              )}
            </Button>

            <Button
              fullWidth
              variant="text"
              sx={{ mt: 1, opacity: 0.7 }}
              onClick={() => navigate(`/eventos/${cart.eventId}`)}
              disabled={submitting}
            >
              Voltar ao evento
            </Button>

            <Typography sx={{ mt: 1.5, fontSize: 11, opacity: 0.5, textAlign: "center" }}>
              Ambiente de testes · Nenhum valor real será cobrado
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
}
